import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import { ChevronDown, ChevronUp } from 'lucide-react';
import FluidSubpageHero from '../components/FluidSubpageHero';
import CtaSection from '../components/CtaSection';
import FaqSchema from '../components/FaqSchema';

export default function Udredning() {
  const { lang, getLocalizedValue } = useLanguage();
  const [cmsFaqs, setCmsFaqs] = useState([]);
  const [cmsPage, setCmsPage] = useState(null);

  useEffect(() => {
    const fetchPageData = async () => {
      try {
        let res = await fetch('/_api/public/content/udredning_side').catch(() => null);
        if (!res || !res.ok) {
          res = await fetch('https://api.dev.auxo.dk/public/content/udredning_side');
        }
        if (res && res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            setCmsPage(data[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching udredning_side:', err);
      }
    };
    fetchPageData();
  }, [lang]);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        let res = await fetch('https://api.dev.auxo.dk/public/v2/content/ofte_stillede_spoergsmaal?term=faq-kategori:udredning&limit=7&sort=sortering&order=asc');
        if (!res.ok) {
          res = await fetch('https://api.dev.auxo.dk/public/v2/content/faq?term=faq-kategori:udredning&limit=7&sort=order&order=asc');
        }
        if (res.ok) {
          const data = await res.json();
          if (data.items && data.items.length > 0) {
            setCmsFaqs(data.items);
          }
        }
      } catch (err) {
        console.error('Error fetching FAQs in Udredning:', err);
      }
    };
    fetchFaqs();
  }, [lang]);

  const seoTitle = lang === 'da' 
    ? 'Psykiatrisk Udredning af ADHD & Autisme | Nordisk Psykiatri' 
    : 'Psykiatrisk Utredning av ADHD & Autism | Nordisk Psykiatri';

  const seoDesc = lang === 'da'
    ? 'Grundig psykiatrisk udredning af unge og voksne for ADHD, autisme og andre tilstande. Gennemskuelig proces og hurtig opstart i København.'
    : 'Noggrann psykiatrisk utredning av unga och vuxna för ADHD, autism och andra tillstånd. Tydlig process och snabb start i Köpenhamn.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });

  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  // Localized texts
  const content = {
    da: {
      hero: {
        eyebrow: 'UDREDNING',
        title: 'Grundig psykiatrisk udredning af unge og voksne',
        desc: 'Hos Nordisk Psykiatri i København tilbyder vi grundig udredning for blandt andet ADHD og autisme. Udredningen giver en nuanceret forståelse af dine symptomer, styrker og udfordringer og danner grundlag for den rette behandling.',
        cta: 'Book indledende samtale'
      },
      what: {
        title: 'Hvad kan vi udrede?',
        intro: 'Vi tilbyder psykiatrisk udredning af unge og voksne for en bred vifte af psykiske lidelser og tilstande.',
        cards: [
          {
            title: 'ADHD / ADD',
            desc: 'Udredning af opmærksomhedsforstyrrelser, impulsivitet og hyperaktivitet',
            link: '/adhd',
            icon: 'fa-brain'
          },
          {
            title: 'Autismespektrum (ASF)',
            desc: 'Udredning af gennemgribende udviklingsforstyrrelser og sensoriske profiler',
            link: '/autisme',
            icon: 'fa-shapes'
          },
          {
            title: 'Angstlidelser & OCD',
            desc: 'Generaliseret angst, Social angst, Panikangst og OCD',
            link: '/psykiatriske-lidelser/angst',
            icon: 'fa-shield-halved'
          },
          {
            title: 'Affektive lidelser',
            desc: 'Depression, Bipolar lidelse',
            link: '/psykiatriske-lidelser/depression',
            icon: 'fa-cloud-sun'
          },
          {
            title: 'Personligheds-forstyrrelser',
            desc: 'Udredning af personlighedsstruktur og relationelle mønstre',
            link: '/psykiatriske-lidelser/personlighedsforstyrrelser',
            icon: 'fa-user-gear'
          },
          {
            title: 'Traumer og PTSD',
            desc: 'PTSD, Kompleks PTSD, Belastningsreaktioner',
            link: '/psykiatriske-lidelser/ptsd',
            icon: 'fa-heart-pulse'
          },
          {
            title: 'Begavelse og kognitive styrker',
            desc: 'Udredning af begavelse og kognitive profiler (via WAIS).',
            link: null,
            icon: 'fa-graduation-cap'
          },
          {
            title: 'Medicinsk behandling',
            desc: 'Vurdering, opstart og opfølgning af medicinsk behandling.',
            link: '/medicinsk-behandling',
            icon: 'fa-pills'
          }
        ]
      },
      process: {
        title: 'Sådan foregår en psykiatrisk udredning',
        intro: 'Udredningsforløbet tilpasses altid dine behov og den konkrete problemstilling.',
        steps: [
          {
            num: '1',
            title: 'Indledende samtale',
            desc: 'Vi taler om din baggrund, dine udfordringer og dine ønsker til udredningen.',
            svg: (
              <svg className="w-7 h-7 text-petrol-dark" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="10" y="14" width="44" height="30" rx="5"/>
                <path d="m22 44-7 9 1-9M20 27h24"/>
              </svg>
            )
          },
          {
            num: '2',
            title: 'Kliniske interviews',
            desc: 'Strukturerede samtaler med psykolog og/eller læge.',
            svg: (
              <svg className="w-7 h-7 text-petrol-dark" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="17" y="10" width="30" height="44" rx="3"/>
                <path d="M24 20h16M24 29h16M24 38h10"/>
              </svg>
            )
          },
          {
            num: '3',
            title: 'Testning og undersøgelser',
            desc: 'Relevante tests, spørgeskemaer og eventuelle kognitive undersøgelser.',
            svg: (
              <svg className="w-7 h-7 text-petrol-dark" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 50V33h8v17M28 50V22h8v28M42 50V13h8v37"/>
              </svg>
            )
          },
          {
            num: '4',
            title: 'Tværfaglig konference',
            desc: 'Vores specialister gennemgår alle data sammen for at sikre en præcis vurdering.',
            svg: (
              <svg className="w-7 h-7 text-petrol-dark" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="23" cy="23" r="7"/>
                <circle cx="42" cy="24" r="6"/>
                <path d="M10 50c2-10 7-15 13-15s11 5 13 15M34 49c1-7 5-11 10-11 6 0 10 4 11 11"/>
              </svg>
            )
          },
          {
            num: '5',
            title: 'Tilbagemelding og rapport',
            desc: 'Du får grundig mundtlig feedback og en skriftlig rapport med anbefalinger.',
            svg: (
              <svg className="w-7 h-7 text-petrol-dark" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 9h24l8 8v38H17Z"/>
                <path d="M41 9v10h8M24 29h18M24 37h18M24 45h12"/>
              </svg>
            )
          }
        ]
      },
      tools: {
        title: 'Hvilke redskaber anvender vi?',
        desc: 'Vi anvender anerkendte og evidensbaserede redskaber ud fra en individuel klinisk vurdering.',
        tags: ['DIVA-5', 'ADOS-2', 'AAA', 'RAADS-R', 'SCID-5', 'WAIS', 'PSE', 'Spørgeskemaer', 'Observation'],
        qualityTitle: 'Faglighed og kvalitet',
        qualityDesc: 'Vores udredninger følger gældende faglige retningslinjer og opdateres løbende i tråd med ny viden og forskning.'
      },
      faq: {
        title: 'Ofte stillede spørgsmål',
        items: [
          {
            q: 'Hvor lang tid varer en udredning?',
            a: 'Varigheden afhænger af problemstillingen og det konkrete forløb.'
          },
          {
            q: 'Skal jeg have en henvisning?',
            a: 'Kontakt klinikken for at høre nærmere om dine muligheder.'
          },
          {
            q: 'Hvem foretager udredningen?',
            a: 'Udredningen varetages af relevante autoriserede fagpersoner.'
          },
          {
            q: 'Hvad koster en udredning?',
            a: 'Prisen afhænger af udredningstype og omfang. Se vores prisside for detaljerede priser.'
          },
          {
            q: 'Kan pårørende deltage i udredningen?',
            a: 'Pårørende kan inddrages, når det er relevant og aftalt.'
          },
          {
            q: 'Får jeg en rapport?',
            a: 'Ja, der udarbejdes en skriftlig rapport med konklusion og anbefalinger.'
          },
          {
            q: 'Hvad sker der efter udredningen?',
            a: 'Du får en tilbagemelding og forslag til næste skridt.'
          }
        ]
      },
      cta: {
        title: 'Klar til at komme videre?',
        desc: 'Book en indledende samtale, så vi sammen kan finde ud af, om en udredning er det rette for dig.',
        btn: 'Book indledende samtale'
      },
      after: {
        eyebrow: 'EFTER DEN PSYKIATRISKE UDREDNING - HVAD NU?',
        title: 'Fra udredning til næste skridt',
        desc1: 'En psykiatrisk udredning markerer ofte begyndelsen på et nyt kapitel. For nogle er diagnosen en lettelse og en forklaring på udfordringer, de har oplevet gennem mange år. For andre vækker den spørgsmål, refleksion eller blandede følelser.',
        desc2: 'Uanset udfaldet er vores mål, at du går fra udredningen med større forståelse, klarhed og en plan for de næste skridt.',
        cards: [
          {
            title: 'Diagnose og forståelse',
            desc: 'Vi gennemgår resultatet af udredningen sammen med dig og hjælper dig med at forstå, hvad konklusionen betyder i netop din hverdag.'
          },
          {
            title: 'Individuelle anbefalinger',
            desc: 'Du modtager konkrete anbefalinger til behandling, støtte, struktur og eventuelle tilpasninger i arbejde eller uddannelse.'
          },
          {
            title: 'Vejen videre',
            desc: 'Hvis det er relevant, tilbyder vi medicinsk behandling, psykoterapi eller psykoedukation. Du modtager en grundig skriftlig rapport, som kan bruges i dialog med egen læge eller andre relevante samarbejdspartnere.'
          }
        ],
        split: {
          col1: {
            title: 'En ny virkelighed',
            p1: 'Når du som voksen får en diagnose, kan det have stor betydning for din selvforståelse, dine relationer og din hverdag. Det er en proces, der ofte indebærer en blanding af lettelse, sorg og refleksion.',
            p2: '<strong>Den manglende brik i puslespillet</strong><br>Diagnosen kan være en forklaring på de udfordringer, du har oplevet gennem livet. Du har måske følt dig forkert, doven eller ude af stand til at klare det samme som andre. Her kan diagnosen være en lettelse, fordi den giver dig en faglig bekræftelse på, at dine udfordringer har haft en reel årsag – det har ikke handlet om, at du ikke ville.',
            p3: '<strong>Ny selvopfattelse</strong><br>Diagnosen kan føre til en ny forståelse af dig selv og din historie – med både lettelse og sorg over det, der har været svært. Det er en proces. Og det er helt normalt at have brug for tid.'
          },
          col2: {
            title: 'Hvordan fortæller jeg det til folk omkring mig?',
            p1: 'At vænne sig til tanken om din diagnose er én ting – at fortælle andre om den er en anden. Det kan føles svært at dele, fordi du måske frygter, at omgivelserne vil se anderledes på dig.',
            p2: 'Det kan være en fordel at være åben om din diagnose – og det vil altid være en personlig beslutning, om og hvem du vælger at dele den med. Åbenhed kan skabe større forståelse, mindske misforståelser og gøre det lettere at få den støtte, du har brug for.',
            p3: 'En måde at håndtere usikkerheden på kan være at høre, hvordan andre har gjort det – fx gennem fællesskaber eller gruppeforløb.'
          }
        },
        quote: {
          text: 'Formålet med en psykiatrisk udredning er ikke at sætte en etiket på dig, men at give dig og dine omgivelser en dybere forståelse for dine styrker og udfordringer.',
          sub: 'Felix Enghoff Klich, Klinisk psykolog'
        },
        advice: {
          title: '3 gode råd fra psykologen',
          items: [
            {
              title: 'Tal med andre om det',
              desc: 'Del dine tanker med nogen, der forstår dig – enten venner, et fællesskab eller en fagperson. At tale med andre og høre andres erfaringer kan gøre det lettere at acceptere og dele din diagnose.'
            },
            {
              title: 'Opsøg viden om din diagnose',
              desc: 'Brug bøger, podcasts, artikler og foreninger til at lære mere om din diagnose og finde strategier, der passer til dig.'
            },
            {
              title: 'Husk, at det er en proces',
              desc: 'Det er normalt at opleve en periode med forvirring og blandede følelser. For mange fylder det mindre med tiden, efterhånden som de opnår større forståelse og finder en ny balance.'
            }
          ]
        },
        cta: {
          title: 'Vil du vide mere?',
          desc: 'Vi anbefaler ADHD Foreningen, Autismeforeningen og andre relevante organisationer, hvor du kan finde viden, netværk og events.',
          btn: 'Se anbefalede links'
        }
      }
    },
    sv: {
      hero: {
        eyebrow: 'UTREDNING',
        title: 'Psykiatrisk utredning med noggrannhet och insikt',
        desc: 'En psykiatrisk utredning ger dig en nyanserad förståelse av dina symptom, styrkor och utmaningar – och skapar grunden för rätt behandling.',
        cta: 'Boka inledande samtal'
      },
      what: {
        title: 'Vad kan vi utreda?',
        intro: 'Vi utreder ett brett spektrum av psykiska tillstånd och diagnoser.',
        cards: [
          {
            title: 'ADHD / ADD',
            desc: 'Utredning av uppmärksamhetsstörning, impulsivitet och hyperaktivitet',
            link: '/adhd',
            icon: 'fa-brain'
          },
          {
            title: 'Autismespektrum (ASF)',
            desc: 'Utredning av genomgripande utvecklingsstörningar och sensoriska profiler',
            link: '/autisme',
            icon: 'fa-shapes'
          },
          {
            title: 'Angstlidelser & OCD',
            desc: 'Generaliserat ångestsyndrom, Social ångest, Panikångest och OCD',
            link: '/psykiatriske-lidelser/angst',
            icon: 'fa-shield-halved'
          },
          {
            title: 'Affektive lidelser',
            desc: 'Depression, Bipolär sjukdom',
            link: '/psykiatriske-lidelser/depression',
            icon: 'fa-cloud-sun'
          },
          {
            title: 'Personligheds-forstyrrelser',
            desc: 'Utredning av personlighetsstruktur och relationella mönster',
            link: '/psykiatriske-lidelser/personlighedsforstyrrelser',
            icon: 'fa-user-gear'
          },
          {
            title: 'Traumer og PTSD',
            desc: 'PTSD, Komplext PTSD, Belastningsreaktioner',
            link: '/psykiatriske-lidelser/ptsd',
            icon: 'fa-heart-pulse'
          },
          {
            title: 'Begavelse og kognitive styrker',
            desc: 'Utredning av begåvning och kognitiva profiler (via WAIS).',
            link: null,
            icon: 'fa-graduation-cap'
          },
          {
            title: 'Medicinsk behandling',
            desc: 'Bedömning, insättning och uppföljning av medicinsk behandling.',
            link: '/medicinsk-behandling',
            icon: 'fa-pills'
          }
        ]
      },
      process: {
        title: 'Så här går utredningen till',
        intro: 'Processen anpassas alltid efter dina behov och den specifika frågeställningen.',
        steps: [
          {
            num: '1',
            title: 'Inledande samtal',
            desc: 'Vi pratar om din bakgrund, dina utmaningar och dina önskemål med utredningen.',
            svg: (
              <svg className="w-7 h-7 text-petrol-dark" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="10" y="14" width="44" height="30" rx="5"/>
                <path d="m22 44-7 9 1-9M20 27h24"/>
              </svg>
            )
          },
          {
            num: '2',
            title: 'Kliniska intervjuer',
            desc: 'Strukturerade samtal med psykolog och/eller specialistläkare.',
            svg: (
              <svg className="w-7 h-7 text-petrol-dark" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="17" y="10" width="30" height="44" rx="3"/>
                <path d="M24 20h16M24 29h16M24 38h10"/>
              </svg>
            )
          },
          {
            num: '3',
            title: 'Tester och utredningar',
            desc: 'Relevanta tester, frågeformulär och eventuella kognitiva undersökningar.',
            svg: (
              <svg className="w-7 h-7 text-petrol-dark" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 50V33h8v17M28 50V22h8v28M42 50V13h8v37"/>
              </svg>
            )
          },
          {
            num: '4',
            title: 'Multidisciplinär konferens',
            desc: 'Våra specialister går igenom all data tillsammans för att säkerställa en exakt bedömning.',
            svg: (
              <svg className="w-7 h-7 text-petrol-dark" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="23" cy="23" r="7"/>
                <circle cx="42" cy="24" r="6"/>
                <path d="M10 50c2-10 7-15 13-15s11 5 13 15M34 49c1-7 5-11 10-11 6 0 10 4 11 11"/>
              </svg>
            )
          },
          {
            num: '5',
            title: 'Återkoppling och rapport',
            desc: 'Du får en noggrann muntlig återkoppling och en skriftlig rapport med rekommendationer.',
            svg: (
              <svg className="w-7 h-7 text-petrol-dark" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 9h24l8 8v38H17Z"/>
                <path d="M41 9v10h8M24 29h18M24 37h18M24 45h12"/>
              </svg>
            )
          }
        ]
      },
      tools: {
        title: 'Vilka verktyg använder vi?',
        desc: 'Vi använder erkända och evidensbaserade verktyg utifrån en individuell klinisk bedömning.',
        tags: ['DIVA-5', 'ADOS-2', 'AAA', 'RAADS-R', 'SCID-5', 'WAIS', 'PSE', 'Frågeformulär', 'Observation'],
        qualityTitle: 'Professionalitet och kvalitet',
        qualityDesc: 'Våra utredningar följer gällande kliniska riktlinjer och uppdateras löpande i takt med ny kunskap och forskning.'
      },
      faq: {
        title: 'Vanliga frågor',
        items: [
          {
            q: 'Hur lång tid tar en utredning?',
            a: 'Tidsåtgången beror på frågeställningen och det specifika förloppet.'
          },
          {
            q: 'Behöver jag en remiss?',
            a: 'Kontakta kliniken för att få veta mer om dina möjligheter.'
          },
          {
            q: 'Vem utför utredningen?',
            a: 'Utredningen utförs av legitimerade specialister.'
          },
          {
            q: 'Vad kostar en utredning?',
            a: 'Priset beror på utredningens typ och omfattning. Se vår prissida för detaljerade priser.'
          },
          {
            q: 'Kan anhöriga delta i utredningen?',
            a: 'Anhöriga kan involveras när det är relevant och överenskommet.'
          },
          {
            q: 'Får jag en rapport?',
            a: 'Ja, en skriftlig rapport med slutsatser och rekommendationer sammanställs.'
          },
          {
            q: 'Vad händer efter utredningen?',
            a: 'Du får en återkoppling och förslag på nästa steg.'
          }
        ]
      },
      cta: {
        title: 'Redo att komma vidare?',
        desc: 'Boka ett inledande samtal, så kan vi tillsammans ta reda på om en utredning är rätt för dig.',
        btn: 'Boka inledande samtal'
      },
      after: {
        eyebrow: 'Efter utredningen – vad nu?',
        title: 'Från insikt till nästa steg',
        desc1: 'En psykiatrisk utredning markerar ofta början på ett nytt kapitel. För vissa är diagnosen en lättnad och en förklaring till utmaningar de har upplevt under många år. För andra väcker den frågor, reflektion eller blandade känslor.',
        desc2: 'Oavsett resultat är vårt mål att du lämnar utredningen med större förståelse, klarhet och en plan för nästa steg.',
        cards: [
          {
            title: 'Diagnos och förståelse',
            desc: 'Vi går igenom resultatet av utredningen tillsammans med dig och hjälper dig att förstå vad slutsatsen innebär i just din vardag.'
          },
          {
            title: 'Individuella rekommendationer',
            desc: 'Du får konkreta rekommendationer för behandling, stöd, struktur och eventuella anpassningar i arbete eller utbildning.'
          },
          {
            title: 'Vägen vidare',
            desc: 'Om det är relevant erbjuder vi medicinsk behandling, psykoterapi eller psykoedukation. Du får en grundlig skriftlig rapport som kan användas i dialog med din husläkare eller andra relevanta samarbetspartners.'
          }
        ],
        split: {
          col1: {
            title: 'En ny verklighet',
            p1: 'När du som vuxen får en diagnos kan det ha stor betydelse för din självbild, dina relationer och din vardag. Det är en process som ofta innebär en blandning av lättnad, sorg och reflektion.',
            p2: '<strong>Den saknade pusselbiten</strong><br>Diagnosen kan vara en förklaring till de utmaningar du har upplevt genom livet. Du har kanske känt dig felaktig, lat eller oförmögen att klara av samma saker som andra. Här kan diagnosen vara en lättnad eftersom den ger dig en professionell bekräftelse på att dina utmaningar har haft en verklig orsak – det har inte handlat om att du inte ville.',
            p3: '<strong>Ny självuppfattning</strong><br>Diagnosen kan leda till en ny förståelse av dig själv och din historia – med både lättnad och sorg över det som har varit svårt. Det är en process. Och det är helt normalt att behöva tid.'
          },
          col2: {
            title: 'Hur berättar jag det för människor i min omgivning?',
            p1: 'Att vänja sig vid tanken på din diagnos är en sak – att berätta för andra är en annan. Det kan kännas svårt att dela eftersom du kanske fruktar att omgivningen kommer att se annorlunda på dig.',
            p2: 'Det kan vara en fördel att vara öppen med din diagnos – och det kommer alltid att vara ett personligt beslut om och vem du väljer att dela den med. Öppenhet kan skapa större förståelse, minska missförstånd och göra det lättare att få det stöd du behöver.',
            p3: 'Ett sätt att hantera osäkerheten kan vara att höra hur andra har gjort – t.ex. genom gemenskaper eller gruppträffar.'
          }
        },
        quote: {
          text: 'En diagnos beskriver en del av din historia – inte hela din historia.',
          sub: 'Syftet med en psykiatrisk utredning är inte att sätta en etikett på dig, utan att skapa större förståelse, ge en solid professionell grund och hjälpa dig vidare med de möjligheter som passar just din situation.'
        },
        advice: {
          title: '3 goda råd från psykologen',
          items: [
            {
              title: 'Prata med andra om det',
              desc: 'Dela dina tankar med någon som förstår dig – antingen vänner, en gemenskap eller en professionell. Att prata med andra och höra andras erfarenheter kan göra det lättare att acceptera och dela din diagnos.'
            },
            {
              title: 'Sök kunskap om din diagnos',
              desc: 'Använd böcker, poddar, artiklar och föreningar för att lära dig mer om din diagnos och hitta strategier som passar dig.'
            },
            {
              title: 'Kom ihåg att det är en process',
              desc: 'Det är normalt att uppleva en period av förvirring och blandade känslor. Det går över – och med tiden hittar många en ny balans och bättre välbefinnande.'
            }
          ]
        },
        cta: {
          title: 'Vill du veta mer?',
          desc: 'Vi rekommenderar ADHD-föreningen, Autism- och Aspergerförbundet och andra relevanta organisationer där du kan hitta kunskap, nätverk och evenemang.',
          btn: 'Se rekommenderade länkar'
        }
      }
    },
    en: {
      meta: {
        title: 'Psychiatric Assessment - ADHD, Autism & Other Conditions | Nordisk Psykiatri',
        desc: 'Thorough and systematic psychiatric assessment tailored to your needs. Specialized in ADHD and autism.'
      },
      hero: {
        eyebrow: 'PSYCHIATRIC ASSESSMENT',
        title: 'Assessment of ADHD, Autism and other psychiatric conditions',
        desc: 'A thorough and professional assessment gives you clarity about your challenges, your strengths, and the best path forward.',
        cta: 'Book initial consultation'
      },
      what: {
        title: 'What can we assess?',
        intro: 'We offer specialized psychiatric assessments for adolescents and adults within a wide range of conditions.',
        cards: [
          {
            title: 'ADHD',
            desc: 'Assessment of attention, hyperactivity, and impulsivity in adolescents and adults.',
            link: '/adhd',
            icon: 'fa-brain'
          },
          {
            title: 'Autism',
            desc: 'Assessment of social interaction, communication, and sensory processing.',
            link: '/autisme',
            icon: 'fa-puzzle-piece'
          },
          {
            title: 'Anxiety disorders',
            desc: 'Assessment of generalized anxiety, panic disorder, social anxiety, and phobias.',
            link: '/psykiatriske-lidelser/angst',
            icon: 'fa-heart-pulse'
          },
          {
            title: 'Depression',
            desc: 'Assessment of mood disorders, severe sadness, and recurrent depressive episodes.',
            link: '/psykiatriske-lidelser/depression',
            icon: 'fa-cloud-rain'
          },
          {
            title: 'OCD',
            desc: 'Assessment of obsessive thoughts and compulsive behaviors.',
            link: '/psykiatriske-lidelser/ocd',
            icon: 'fa-rotate'
          },
          {
            title: 'PTSD and stress reactions',
            desc: 'Assessment of post-traumatic stress and acute or prolonged stress reactions.',
            link: '/psykiatriske-lidelser/ptsd',
            icon: 'fa-shield-halved'
          },
          {
            title: 'Personality disorders',
            desc: 'Assessment of persistent emotional and relational patterns.',
            link: '/psykiatriske-lidelser/personlighedsforstyrrelser',
            icon: 'fa-user-group'
          },
          {
            title: 'Cognitive testing',
            desc: 'Assessment of intellectual and cognitive profiles (via WAIS).',
            link: null,
            icon: 'fa-graduation-cap'
          },
          {
            title: 'Medical treatment',
            desc: 'Evaluation, initiation, and follow-up of psychiatric medication.',
            link: '/medicinsk-behandling',
            icon: 'fa-pills'
          }
        ]
      },
      process: {
        title: 'How a psychiatric assessment takes place',
        intro: 'The assessment pathway is always tailored to your specific needs and clinical presentation.',
        steps: [
          {
            num: '1',
            title: 'Initial consultation',
            desc: 'We discuss your background, challenges, and goals for the assessment.',
            svg: (
              <svg className="w-7 h-7 text-petrol-dark" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="10" y="14" width="44" height="30" rx="5"/>
                <path d="m22 44-7 9 1-9M20 27h24"/>
              </svg>
            )
          },
          {
            num: '2',
            title: 'Clinical interviews',
            desc: 'Structured diagnostic interviews with a psychologist and/or psychiatrist.',
            svg: (
              <svg className="w-7 h-7 text-petrol-dark" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="17" y="10" width="30" height="44" rx="3"/>
                <path d="M24 20h16M24 29h16M24 38h10"/>
              </svg>
            )
          },
          {
            num: '3',
            title: 'Testing & examinations',
            desc: 'Relevant standardized tests, questionnaires, and potential cognitive assessments.',
            svg: (
              <svg className="w-7 h-7 text-petrol-dark" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 50V33h8v17M28 50V22h8v28M42 50V13h8v37"/>
              </svg>
            )
          },
          {
            num: '4',
            title: 'Multidisciplinary conference',
            desc: 'Our specialists review all clinical data together to ensure an accurate diagnosis.',
            svg: (
              <svg className="w-7 h-7 text-petrol-dark" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="23" cy="23" r="7"/>
                <circle cx="43" cy="23" r="7"/>
                <path d="M12 47v-4c0-5 5-8 11-8s11 3 11 8v4M32 47v-4c0-5 5-8 11-8s11 3 11 8v4"/>
              </svg>
            )
          },
          {
            num: '5',
            title: 'Feedback & report',
            desc: 'Comprehensive review of results, written epicrisis, and recommendations for next steps.',
            svg: (
              <svg className="w-7 h-7 text-petrol-dark" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="32" cy="32" r="22"/>
                <path d="m23 32 6 6 12-12"/>
              </svg>
            )
          }
        ]
      },
      tools: {
        title: 'Which tools do we use?',
        desc: 'We utilize recognized and evidence-based diagnostic tools based on an individual clinical evaluation.',
        tags: [
          'DIVA-5',
          'ADOS-2',
          'AAA',
          'RAADS-R',
          'SCID-5',
          'WAIS',
          'PSE',
          'Questionnaires',
          'Clinical observation'
        ],
        qualityTitle: 'Professional integrity & quality',
        qualityDesc: 'Our assessments adhere to current national guidelines and are performed by certified healthcare professionals.'
      },
      faq: {
        title: 'Frequently asked questions about assessment',
        items: [
          {
            q: 'How long does an assessment take?',
            a: 'A typical assessment pathway spans 3-4 weeks from initial consultation to final feedback.'
          },
          {
            q: 'Do I need a referral from my GP?',
            a: 'No, as a private clinic we do not require a GP referral. You can contact us directly.'
          },
          {
            q: 'Does health insurance cover the assessment?',
            a: 'Many private health insurance plans cover psychiatric assessments in whole or in part. Contact your insurer to confirm coverage.'
          },
          {
            q: 'Will I receive a written report?',
            a: 'Yes, a comprehensive written clinical report with diagnostic conclusions and recommendations is provided.'
          },
          {
            q: 'What happens after the assessment?',
            a: 'You receive thorough feedback and clear recommendations for your next steps.'
          }
        ]
      },
      cta: {
        title: 'Ready to move forward?',
        desc: 'Book an initial consultation so we can determine together whether an assessment is right for you.',
        btn: 'Book initial consultation'
      },
      after: {
        eyebrow: 'AFTER THE PSYCHIATRIC ASSESSMENT - WHAT NEXT?',
        title: 'From assessment to your next step',
        desc1: 'A psychiatric assessment often marks the beginning of a new chapter. For some, a diagnosis brings relief and an explanation for lifelong challenges. For others, it prompts questions, reflection, or mixed emotions.',
        desc2: 'Regardless of the outcome, our goal is that you leave the assessment with greater understanding, clarity, and a clear plan for your next steps.',
        cards: [
          {
            title: 'Diagnosis & understanding',
            desc: 'We review the results together with you and help you understand what the conclusion means in your everyday life.'
          },
          {
            title: 'Individual recommendations',
            desc: 'You receive concrete recommendations for treatment, support, structure, and potential workplace or educational accommodations.'
          },
          {
            title: 'The path ahead',
            desc: 'If relevant, we offer medical treatment, psychotherapy, or psychoeducation. You receive a thorough written report that can be shared with your GP or other partners.'
          }
        ],
        split: {
          col1: {
            title: 'A new reality',
            p1: 'Receiving a diagnosis as an adult can have a significant impact on your self-perception, relationships, and daily life. It is a process often involving a mixture of relief, grief, and reflection.',
            p2: '<strong>The missing puzzle piece</strong><br>The diagnosis can explain challenges you have experienced throughout life. You may have felt flawed, lazy, or unable to cope like others. Here, the diagnosis brings relief by confirming that your struggles had a genuine cause – it was never about a lack of effort.',
            p3: '<strong>A new self-understanding</strong><br>The diagnosis can lead to a transformed understanding of yourself and your story – with both relief and grief for what has been hard. It is a process, and taking time is completely natural.'
          },
          col2: {
            title: 'Recommendations and guidance',
            p1: 'Sharing your diagnosis is a personal choice. Openness can foster understanding and ease everyday interactions, but it can also be wise to share thoughtfully with people you trust.',
            p2: 'Seeking knowledge through reputable books, podcasts, and patient organizations can provide valuable perspectives, practical strategies, and a sense of community.',
            p3: 'Remember that adapting to a diagnosis is a process. Over time, many experience that clarity and self-insight open up new opportunities and improved well-being.'
          }
        },
        quote: {
          text: 'The purpose of a psychiatric assessment is not to label you, but to give you and those around you a deeper understanding of your strengths and challenges.',
          sub: 'Felix Enghoff Klich, Clinical Psychologist'
        },
        advice: {
          title: '3 recommendations from the psychologist',
          items: [
            {
              title: 'Talk to others about it',
              desc: 'Share your thoughts with someone who understands – friends, a community, or a professional. Hearing others’ experiences can make it easier to accept and integrate your diagnosis.'
            },
            {
              title: 'Seek knowledge about your diagnosis',
              desc: 'Use books, podcasts, articles, and associations to learn more and identify everyday strategies that work for you.'
            },
            {
              title: 'Remember that it is a process',
              desc: 'It is natural to experience confusion or mixed emotions. For most, this eases over time as clarity, insight, and a new equilibrium develop.'
            }
          ]
        },
        cta: {
          title: 'Want to learn more?',
          desc: 'We recommend relevant patient associations and organizations where you can find knowledge, networks, and events.',
          btn: 'See recommended links'
        }
      }
    }
  };

  const t = content[lang] || (lang === 'en' ? content.en : null) || content.da;

  const heroBadge = cmsPage ? getLocalizedValue(cmsPage.hero_badge) || t.hero.eyebrow : t.hero.eyebrow;
  const heroTitle = cmsPage ? getLocalizedValue(cmsPage.hero_overskrift) || t.hero.title : t.hero.title;
  const heroDesc = cmsPage ? getLocalizedValue(cmsPage.hero_beskrivelse) || t.hero.desc : t.hero.desc;
  const heroCtaText = cmsPage ? getLocalizedValue(cmsPage.hero_cta_tekst) || t.hero.cta : t.hero.cta;
  const heroCtaLink = (cmsPage && cmsPage.hero_cta_link) || "/kontakt";

  const quoteText = cmsPage ? getLocalizedValue(cmsPage.citat_tekst) || (t.after?.quote?.text || '') : (t.after?.quote?.text || '');
  const quoteSub = cmsPage ? getLocalizedValue(cmsPage.citat_forfatter) || (t.after?.quote?.sub || '') : (t.after?.quote?.sub || '');

  const whatTitle = cmsPage ? getLocalizedValue(cmsPage.hvad_kan_udredes_overskrift) || t.what.title : t.what.title;
  const whatIntro = cmsPage ? getLocalizedValue(cmsPage.hvad_kan_udredes_undertekst) || t.what.intro : t.what.intro;

  const processTitle = cmsPage ? getLocalizedValue(cmsPage.proces_overskrift) || t.process.title : t.process.title;
  const processIntro = cmsPage ? getLocalizedValue(cmsPage.proces_undertekst) || t.process.intro : t.process.intro;

  const ctaTitle = cmsPage ? getLocalizedValue(cmsPage.cta_overskrift) || t.cta.title : t.cta.title;
  const ctaDesc = cmsPage ? getLocalizedValue(cmsPage.cta_beskrivelse) || t.cta.desc : t.cta.desc;
  const ctaButton = cmsPage ? getLocalizedValue(cmsPage.cta_knap_tekst) || t.cta.button || t.cta.btn : (t.cta.button || t.cta.btn);
  const ctaLink = (cmsPage && cmsPage.cta_knap_link) || "/kontakt";

  const efterEyebrow = cmsPage ? getLocalizedValue(cmsPage.efter_eyebrow) || t.after.eyebrow : t.after.eyebrow;
  const efterTitle = cmsPage ? getLocalizedValue(cmsPage.efter_overskrift) || t.after.title : t.after.title;
  const efterDesc1 = cmsPage ? getLocalizedValue(cmsPage.efter_beskrivelse_1) || t.after.desc1 : t.after.desc1;
  const efterDesc2 = cmsPage ? getLocalizedValue(cmsPage.efter_beskrivelse_2) || t.after.desc2 : t.after.desc2;
  
  const formatImageUrl = (img, width = 1200, fallback = '') => {
    const raw = Array.isArray(img) ? img[0] : img;
    if (!raw || typeof raw !== 'string') return fallback;
    const trimmed = raw.trim();
    if (!trimmed) return fallback;
    if (trimmed.includes('/cdn-cgi/image/')) return trimmed;
    if (trimmed.startsWith('https://assets.auxodev.dk/')) {
      return trimmed.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`);
    }
    if (trimmed.startsWith('/_assets/')) {
      return `/cdn-cgi/image/width=${width},quality=85,format=auto${trimmed}`;
    }
    return trimmed;
  };

  const heroImage = formatImageUrl(
    cmsPage?.hero_billede,
    1600,
    "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/samtalerum-nordisk-psykiatri-vesterbrogade-v4-varmere.jpg"
  );

  const efterImage = formatImageUrl(
    cmsPage?.efter_billede,
    1000,
    "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/scandinavian-calm-water.jpg"
  );

  const efterCards = [
    {
      title: cmsPage ? getLocalizedValue(cmsPage.efter_kort_1_titel) || t.after?.cards?.[0]?.title : t.after?.cards?.[0]?.title,
      desc: cmsPage ? getLocalizedValue(cmsPage.efter_kort_1_tekst) || t.after?.cards?.[0]?.desc : t.after?.cards?.[0]?.desc
    },
    {
      title: cmsPage ? getLocalizedValue(cmsPage.efter_kort_2_titel) || t.after?.cards?.[1]?.title : t.after?.cards?.[1]?.title,
      desc: cmsPage ? getLocalizedValue(cmsPage.efter_kort_2_tekst) || t.after?.cards?.[1]?.desc : t.after?.cards?.[1]?.desc
    },
    {
      title: cmsPage ? getLocalizedValue(cmsPage.efter_kort_3_titel) || t.after?.cards?.[2]?.title : t.after?.cards?.[2]?.title,
      desc: cmsPage ? getLocalizedValue(cmsPage.efter_kort_3_tekst) || t.after?.cards?.[2]?.desc : t.after?.cards?.[2]?.desc
    }
  ];

  const efterSplitCol1Title = cmsPage ? getLocalizedValue(cmsPage.efter_split_1_titel) || t.after?.split?.col1?.title : t.after?.split?.col1?.title;
  const efterSplitCol1Html = cmsPage ? getLocalizedValue(cmsPage.efter_split_1_tekst) : null;

  const efterSplitCol2Title = cmsPage ? getLocalizedValue(cmsPage.efter_split_2_titel) || t.after?.split?.col2?.title : t.after?.split?.col2?.title;
  const efterSplitCol2Html = cmsPage ? getLocalizedValue(cmsPage.efter_split_2_tekst) : null;

  const parseHvadKanUdredesPunkter = (html) => {
    if (!html || typeof html !== 'string') return null;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const items = Array.from(doc.querySelectorAll('li'));
      if (items.length === 0) return null;

      const parsedCards = [];
      for (const li of items) {
        let rawText = li.textContent || '';
        let link = null;
        let icon = null;

        // Check if there is an <a> tag
        const aTag = li.querySelector('a');
        if (aTag && aTag.getAttribute('href')) {
          link = aTag.getAttribute('href').trim();
        }

        // Check if there are shortcodes like [link: ...] or [icon: ...]
        const linkMatch = rawText.match(/\[(?:link|url):\s*([^\]]+)\]/i);
        if (linkMatch) {
          link = linkMatch[1].trim();
          rawText = rawText.replace(linkMatch[0], '');
        }

        const iconMatch = rawText.match(/\[icon:\s*([^\]]+)\]/i);
        if (iconMatch) {
          icon = iconMatch[1].trim();
          rawText = rawText.replace(iconMatch[0], '');
        }

        // Title and description
        const strongTag = li.querySelector('strong') || li.querySelector('b');
        let title = '';
        let desc = '';

        if (strongTag) {
          title = strongTag.textContent.replace(/[:\-–—\s]+$/, '').trim();
          // Remove strong tag from cloned element to get the rest as desc
          const clone = li.cloneNode(true);
          const strongsInClone = clone.querySelectorAll('strong, b');
          strongsInClone.forEach(s => s.remove());
          let remainingText = clone.textContent || '';
          if (linkMatch) remainingText = remainingText.replace(linkMatch[0], '');
          if (iconMatch) remainingText = remainingText.replace(iconMatch[0], '');
          desc = remainingText.replace(/^[\s:\-–—]+/, '').trim();
        } else {
          // If no strong tag, split by colon if exists
          if (rawText.includes(':')) {
            const parts = rawText.split(':');
            title = parts[0].trim();
            desc = parts.slice(1).join(':').trim();
          } else {
            title = rawText.trim();
            desc = '';
          }
        }

        // Auto-assign default icons & links if not provided in CMS
        // Ikoner er faste på sektionen (baseret på titel eller index)
        const lower = title.toLowerCase();
        if (lower.includes('adhd') || lower.includes('add')) icon = 'fa-brain';
        else if (lower.includes('autis') || lower.includes('asf')) icon = 'fa-shapes';
        else if (lower.includes('angst') || lower.includes('ocd')) icon = 'fa-shield-halved';
        else if (lower.includes('affektiv') || lower.includes('depress') || lower.includes('bipolar')) icon = 'fa-cloud-sun';
        else if (lower.includes('personlighed')) icon = 'fa-user-gear';
        else if (lower.includes('traum') || lower.includes('ptsd')) icon = 'fa-heart-pulse';
        else if (lower.includes('begavel') || lower.includes('wais') || lower.includes('kognitiv') || lower.includes('styrker')) icon = 'fa-graduation-cap';
        else if (lower.includes('medicin') || lower.includes('behandling')) icon = 'fa-pills';
        else {
          // Fast rækkefølge af faste ikoner som fallback
          const defaultIcons = ['fa-brain', 'fa-shapes', 'fa-shield-halved', 'fa-cloud-sun', 'fa-user-gear', 'fa-heart-pulse', 'fa-graduation-cap', 'fa-pills'];
          icon = defaultIcons[parsedCards.length % defaultIcons.length];
        }

        if (link === '' || link === 'null' || link === 'none' || link === '#') {
          link = null;
        }

        if (title) {
          parsedCards.push({
            title,
            desc,
            link,
            icon
          });
        }
      }

      return parsedCards.length > 0 ? parsedCards : null;
    } catch (err) {
      console.error('Error parsing hvad_kan_udredes_punkter:', err);
      return null;
    }
  };

  const parsedCmsPunkter = cmsPage ? parseHvadKanUdredesPunkter(getLocalizedValue(cmsPage.hvad_kan_udredes_punkter)) : null;
  const assessmentCards = parsedCmsPunkter || t.what?.cards || t.what?.items || [];

  const raadOverskrift = cmsPage ? getLocalizedValue(cmsPage.raad_overskrift) || t.after?.advice?.title : t.after?.advice?.title;
  const raadItems = [
    {
      title: cmsPage ? getLocalizedValue(cmsPage.raad_1_titel) || t.after?.advice?.items?.[0]?.title : t.after?.advice?.items?.[0]?.title,
      desc: cmsPage ? getLocalizedValue(cmsPage.raad_1_tekst) || t.after?.advice?.items?.[0]?.desc : t.after?.advice?.items?.[0]?.desc
    },
    {
      title: cmsPage ? getLocalizedValue(cmsPage.raad_2_titel) || t.after?.advice?.items?.[1]?.title : t.after?.advice?.items?.[1]?.title,
      desc: cmsPage ? getLocalizedValue(cmsPage.raad_2_tekst) || t.after?.advice?.items?.[1]?.desc : t.after?.advice?.items?.[1]?.desc
    },
    {
      title: cmsPage ? getLocalizedValue(cmsPage.raad_3_titel) || t.after?.advice?.items?.[2]?.title : t.after?.advice?.items?.[2]?.title,
      desc: cmsPage ? getLocalizedValue(cmsPage.raad_3_tekst) || t.after?.advice?.items?.[2]?.desc : t.after?.advice?.items?.[2]?.desc
    }
  ];

  const ctaVidereTitle = cmsPage ? getLocalizedValue(cmsPage.anbefalede_links_overskrift) || t.after.cta.title : t.after.cta.title;
  const ctaVidereDesc = cmsPage ? getLocalizedValue(cmsPage.anbefalede_links_tekst) || t.after.cta.desc : t.after.cta.desc;
  const ctaVidereBtn = cmsPage ? getLocalizedValue(cmsPage.anbefalede_links_knap_tekst) || t.after.cta.btn : t.after.cta.btn;
  const ctaVidereLink = (cmsPage && cmsPage.anbefalede_links_knap_link) || "/vidensbank";

  return (
    <div className="flex flex-col min-h-screen bg-sand-light">
      {/* Hero Section */}
      <FluidSubpageHero
        eyebrow={heroBadge}
        title={heroTitle}
        description={heroDesc}
        ctaText={heroCtaText}
        ctaLink={heroCtaLink}
        secondaryCtaText={lang === 'da' ? 'Se priser' : 'See prices'}
        secondaryCtaLink="/priser"
        imageUrl={heroImage}
        imageAlt={lang === 'da' ? 'Beroligende skandinavisk vandoverflade' : 'Lugn skandinavisk vattenyta'}
        organicShape={false}
        hideBackdrop={true}
        containerClassName="max-w-7xl px-6 lg:px-8"
        reviewStamp={cmsPage?.fagligt_gennemgaaet ? getLocalizedValue(cmsPage.fagligt_gennemgaaet) : (lang === 'da' ? 'Fagligt gennemgået af Felix Klich, klinisk psykolog | Opdateret september 2026' : 'Clinically reviewed by Felix Klich, Clinical Psychologist | Updated September 2026')}
      />

      {/* What we assess Section */}
      <section className="pt-6 sm:pt-8 md:pt-10 pb-16 md:pb-24 bg-sand-light">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-left">
          <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight text-petrol-dark mb-2">
            {whatTitle}
          </h2>
          <p className="text-base text-[#44585a] mb-12">
            {whatIntro}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {(assessmentCards || []).map((card, idx) => {
              const CardContent = (
                <>
                  <div className="flex flex-col items-start">
                    <div className="w-14 h-14 rounded-full bg-[#E3ECE4] flex items-center justify-center text-[#0F382C] mb-6 transition-transform duration-300 group-hover:scale-105">
                      <i className={`fa-solid ${card.icon} text-xl text-[#0F382C] leading-none`} aria-hidden="true" />
                    </div>
                    <h3 className="font-display text-xl sm:text-[22px] font-semibold text-[#0F382C] tracking-tight leading-snug mb-3 group-hover:text-petrol-medium transition-colors">
                      {card.title}
                    </h3>
                    <p className="font-sans text-[14px] sm:text-[15px] leading-relaxed text-[#52655B]">
                      {card.desc}
                    </p>
                  </div>

                  {card.link && (
                    <div className="pt-6">
                      <span className="inline-flex items-center text-[#0F382C] group-hover:text-petrol-medium transition-colors">
                        <svg className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                        </svg>
                      </span>
                    </div>
                  )}
                </>
              );

              return card.link ? (
                <Link 
                  key={idx} 
                  to={card.link}
                  className="group flex flex-col justify-between min-h-[300px] sm:min-h-[320px] p-6 sm:p-7 bg-[#FAFDF9] border border-[#E5EBE3] rounded-2xl transition-all duration-300 hover:border-[#C5D4C3] hover:bg-[#F4F8F3] hover:-translate-y-0.5 hover:shadow-sm"
                >
                  {CardContent}
                </Link>
              ) : (
                <div 
                  key={idx} 
                  className="flex flex-col justify-between min-h-[300px] sm:min-h-[320px] p-6 sm:p-7 bg-[#FAFDF9] border border-[#E5EBE3] rounded-2xl"
                >
                  {CardContent}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 md:py-24 bg-[#D1E1BE] text-petrol-dark border-t border-b border-[#c2d4ad]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-left">
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-petrol-dark mb-2">
            {processTitle}
          </h2>
          <p className="text-base text-petrol-dark/85 mb-12">
            {processIntro}
          </p>

          {/* Steps Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 relative">
            {(t.process?.steps || []).map((step, idx) => (
              <div key={idx} className="relative text-center flex flex-col items-center">
                {/* Connector Line for Desktop */}
                {idx < t.process.steps.length - 1 && (
                  <div className="hidden lg:block absolute left-[60%] right-[-40%] top-[43px] h-[1px] bg-petrol-dark/20 z-0" />
                )}
                
                {/* Step Number */}
                <div className="w-[34px] h-[34px] rounded-full bg-white text-petrol-dark flex items-center justify-center mb-4 relative z-10 font-bold text-sm border border-petrol-dark/20">
                  {step.num}
                </div>

                {/* Icon Wrap */}
                <div className="w-[52px] h-[52px] rounded-full bg-white/80 text-petrol-dark flex items-center justify-center mb-4 border border-petrol-dark/20">
                  {step.svg}
                </div>

                {/* Title & Description */}
                <h3 className="font-sans text-sm font-bold text-petrol-dark mb-2">
                  {step.title}
                </h3>
                <p className="text-xs text-petrol-dark/80 leading-relaxed max-w-[200px]">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Efter udredningen Section */}
      <section className="py-16 md:py-24 bg-sand-light border-b border-[#eee]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-left">
          
          {/* Hero Split */}
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-12 mb-16 items-center">
            <div>
              <span className="text-xs font-bold tracking-wider text-petrol-medium uppercase block mb-4">
                {efterEyebrow}
              </span>
              <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight text-petrol-dark mb-6">
                {efterTitle}
              </h2>
              <p className="text-base sm:text-lg text-[#44585a] leading-relaxed mb-6">
                {efterDesc1}
              </p>
              <p className="text-base sm:text-lg text-[#44585a] leading-relaxed">
                {efterDesc2}
              </p>
            </div>
            <div className="h-[300px] sm:h-[400px] rounded-lg overflow-hidden shadow-sm relative">
              <img 
                src={efterImage} 
                alt={lang === 'da' ? 'Roligt nordisk landskab ved en sø' : 'Lugnt nordiskt landskap vid en sjö'} 
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {efterCards.map((card, idx) => {
              const icons = [
                <i className="fa-light fa-compass text-2xl text-petrol-medium" />,
                <i className="fa-light fa-list-check text-2xl text-petrol-medium" />,
                <i className="fa-light fa-route text-2xl text-petrol-medium" />
              ];
              return (
                <article 
                  key={idx} 
                  className="border border-[#b3cad0] bg-[#CFE0DB] rounded-lg p-8 flex flex-col gap-4 hover:shadow-sm transition-shadow"
                  style={{ backgroundColor: '#CFE0DB' }}
                >
                  <div className="w-12 h-12 rounded-full bg-white/60 flex items-center justify-center shrink-0">
                    {icons[idx]}
                  </div>
                  <div>
                    <h3 className="font-sans text-base font-bold text-petrol-dark mb-2 leading-snug">
                      {card.title}
                    </h3>
                    <p className="text-sm text-[#46595a] leading-relaxed">
                      {card.desc}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>

          {/* Split Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 border border-[#dedbd4] bg-[#fbfaf7] rounded-lg p-8 md:p-12 mb-16">
            <article className="lg:pr-8 lg:border-r lg:border-[#dedbd4]">
              <h3 className="font-display text-2xl sm:text-3xl font-light text-petrol-dark mb-4">
                {efterSplitCol1Title}
              </h3>
              <div className="w-10 h-[2px] bg-petrol-medium mb-6" />
              {efterSplitCol1Html ? (
                <div 
                  className="space-y-4 text-sm text-[#46595a] leading-relaxed [&>p]:mb-4 last:[&>p]:mb-0 [&>strong]:font-bold [&>strong]:text-petrol-dark"
                  dangerouslySetInnerHTML={{ __html: efterSplitCol1Html }}
                />
              ) : (
                <>
                  <p className="text-sm text-[#46595a] leading-relaxed mb-4">
                    {t.after.split.col1.p1}
                  </p>
                  <p className="text-sm text-[#46595a] leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: t.after.split.col1.p2 }} />
                  <p className="text-sm text-[#46595a] leading-relaxed" dangerouslySetInnerHTML={{ __html: t.after.split.col1.p3 }} />
                </>
              )}
            </article>
            <article className="lg:pl-8">
              <h3 className="font-display text-2xl sm:text-3xl font-light text-petrol-dark mb-4">
                {efterSplitCol2Title}
              </h3>
              <div className="w-10 h-[2px] bg-petrol-medium mb-6" />
              {efterSplitCol2Html ? (
                <div 
                  className="space-y-4 text-sm text-[#46595a] leading-relaxed [&>p]:mb-4 last:[&>p]:mb-0 [&>strong]:font-bold [&>strong]:text-petrol-dark"
                  dangerouslySetInnerHTML={{ __html: efterSplitCol2Html }}
                />
              ) : (
                <>
                  <p className="text-sm text-[#46595a] leading-relaxed mb-4">
                    {t.after.split.col2.p1}
                  </p>
                  <p className="text-sm text-[#46595a] leading-relaxed mb-4">
                    {t.after.split.col2.p2}
                  </p>
                  <p className="text-sm text-[#46595a] leading-relaxed">
                    {t.after.split.col2.p3}
                  </p>
                </>
              )}
            </article>
          </div>
        </div>
      </section>

      {/* Quote Section (Full Width) */}
      <section className="py-12 md:py-16 bg-[#1e3a35] text-white border-y border-[#162d29]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-8 items-center">
            <blockquote className="font-display text-2xl sm:text-3xl font-normal text-white relative pl-12 sm:pl-16">
              <span className="absolute left-0 top-[-10px] font-display text-7xl sm:text-8xl text-white/20 leading-none">“</span>
              {quoteText}
            </blockquote>
            <p className="text-sm text-white/85 leading-relaxed">
              {quoteSub}
            </p>
          </div>
        </div>
      </section>

      {/* Advice & CTA Section */}
      <section className="py-16 md:py-24 bg-sand-light border-b border-[#eee]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-left">
          {/* Advice Section */}
          <div className="mb-16">
            <h3 className="font-display text-2xl sm:text-3xl font-light text-petrol-dark mb-8 text-center">
              {raadOverskrift}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {(raadItems || []).map((item, idx) => {
                const icons = [
                  <i className="fa-light fa-comments text-xl text-petrol-medium" />,
                  <i className="fa-light fa-book-open text-xl text-petrol-medium" />,
                  <i className="fa-light fa-hourglass-half text-xl text-petrol-medium" />
                ];
                return (
                  <article key={idx} className="flex gap-5 md:border-r md:last:border-r-0 border-[#dedbd4] md:pr-6 md:last:pr-0">
                    <div className="w-10 h-10 rounded-full bg-[#eeece6] flex items-center justify-center shrink-0">
                      {icons[idx]}
                    </div>
                    <div>
                      <h4 className="font-sans text-sm font-bold text-petrol-dark mb-2">
                        {item.title}
                      </h4>
                      <p className="text-xs text-[#536566] leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          {/* Internal CTA */}
          <div className="border border-[#e4ddd3] bg-gradient-to-br from-[#faf8f5] to-[#f3efe8] rounded-lg p-6 md:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-5 text-left">
              <div className="w-12 h-12 rounded-full bg-petrol-dark text-white flex items-center justify-center shrink-0">
                <i className="fa-light fa-info text-lg" />
              </div>
              <div>
                <h4 className="font-sans text-base font-bold text-petrol-dark mb-1">
                  {ctaVidereTitle}
                </h4>
                <p className="text-xs text-[#526365] leading-relaxed">
                  {ctaVidereDesc}
                </p>
              </div>
            </div>
            <Link
              to={ctaVidereLink}
              className="inline-flex items-center justify-center rounded-full bg-petrol-dark text-white px-6 py-3 font-bold text-sm hover:opacity-90 transition-opacity whitespace-nowrap"
            >
              {ctaVidereBtn} →
            </Link>
          </div>

        </div>
      </section>

      {/* Tools & FAQ Section */}
      <section className="py-16 md:py-24 bg-sand-light">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-left">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.25fr] gap-12 items-stretch">
            
            {/* Left Column: Tools */}
            <div className="bg-[#faf8f4] rounded-lg p-8 flex flex-col justify-between">
              <div>
                <h2 className="font-display text-3xl font-light tracking-tight text-petrol-dark mb-4">
                  {t.tools.title}
                </h2>
                <p className="text-sm text-[#46595a] leading-relaxed">
                  {t.tools.desc}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2.5 my-8">
                  {(t.tools?.tags || []).map((tag, idx) => (
                    <span 
                      key={idx} 
                      className="bg-[#eeece6] text-petrol-dark rounded-full px-3.5 py-1.5 text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Quality Badge */}
              <div className="flex gap-4 border border-[#dedbd4] p-5 rounded-md bg-sand-light">
                <svg className="w-9 h-9 text-petrol-light shrink-0" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M32 7 52 15v14c0 13-8 23-20 28C20 52 12 42 12 29V15Z"/>
                  <path d="m23 31 6 6 12-13"/>
                </svg>
                <div>
                  <strong className="block text-sm font-bold text-petrol-dark mb-1">
                    {t.tools.qualityTitle}
                  </strong>
                  <p className="text-xs text-[#526365] leading-relaxed">
                    {t.tools.qualityDesc}
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column: FAQ Accordion */}
            <div className="bg-[#faf8f4] rounded-lg p-8">
              <FaqSchema 
                items={(cmsFaqs.length > 0 ? cmsFaqs.map(f => ({
                  q: getLocalizedValue(f.spoergsmaal || f.question),
                  a: getLocalizedValue(f.svar || f.answer)
                })) : (t.faq?.items || [])).slice(0, 7)} 
              />
              <h2 className="font-display text-3xl font-bold tracking-tight text-petrol-dark mb-6">
                {cmsPage ? getLocalizedValue(cmsPage.faq_overskrift) || t.faq?.title : t.faq?.title}
              </h2>

              <div className="space-y-3">
                {(cmsFaqs.length > 0 ? cmsFaqs.map(f => ({
                  q: getLocalizedValue(f.spoergsmaal || f.question),
                  a: getLocalizedValue(f.svar || f.answer)
                })) : (t.faq?.items || [])).slice(0, 7).map((item, idx) => {
                  const isOpen = openFaqIndex === idx;
                  return (
                    <div 
                      key={idx} 
                      className={`border-t border-[#d8d5ce] last:border-b border-[#d8d5ce] transition-all duration-200 ${
                        isOpen ? 'py-3' : 'py-1'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleFaq(idx)}
                        aria-expanded={isOpen}
                        className={`flex w-full items-center justify-between text-left font-sans text-sm font-bold text-petrol-dark focus:outline-none transition-all duration-200 cursor-pointer ${
                          isOpen ? 'py-2' : 'py-1.5'
                        }`}
                      >
                        <span>{item.q}</span>
                        {isOpen ? (
                          <ChevronUp className="h-4 w-4 text-petrol-medium shrink-0" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-petrol-medium shrink-0" />
                        )}
                      </button>
                      {/* Bevares altid i DOM for Google og crawlere */}
                      <div 
                        className={`text-xs text-[#566869] leading-relaxed ${
                          isOpen ? 'mt-2 pb-2 block' : 'hidden'
                        }`}
                        dangerouslySetInnerHTML={typeof item.a === 'string' && item.a.includes('<') ? { __html: item.a } : undefined}
                      >
                        {typeof item.a === 'string' && item.a.includes('<') ? null : item.a}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection 
        title={ctaTitle}
        desc={ctaDesc}
        buttonText={ctaButton}
        buttonLink={ctaLink}
      />
    </div>
  );
}
